import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';

const PageContainer = styled.div`
  padding-top: 0px;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  margin: 0 auto;
`;

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Fetch products from the API
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/all-products`);
                
                if (response.data && Array.isArray(response.data)) {
                    setProducts(response.data);
                } else {
                    // Fallback to sample products if API doesn't return expected format
                    setProducts(getSampleProducts());
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching products:", err);
                setError("Failed to load products. Using sample data instead.");
                // Fallback to sample products on error
                setProducts(getSampleProducts());
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Sample products data as fallback
    const getSampleProducts = () => {
        return [
            {
                id: 1,
                name: "Stellar Simplicity",
                description: "Beautiful minimalist design with clean lines and subtle accents",
                price: 39.99,
                style: "basic",
                image_url: "/images/product-1.png"
            },
            {
                id: 2,
                name: "Orbit",
                description: "Elegant circular frame highlighting your celestial moment",
                price: 49.99,
                style: "premium",
                image_url: "/images/product-2.png"
            },
            {
                id: 3,
                name: "Constellation Frame",
                description: "Highlighted constellations with decorative frame",
                price: 49.99,
                style: "premium",
                image_url: "/images/product-3.png"
            },
            {
                id: 4,
                name: "Celestial Corners",
                description: "Geometric design with ornate corner elements",
                price: 49.99,
                style: "premium",
                image_url: "/images/product-4.png"
            },
            {
                id: 5,
                name: "Starlit Halo",
                description: "Radiant circular design with enhanced star visibility",
                price: 59.99,
                style: "deluxe",
                image_url: "/images/product-5.png"
            },
            {
                id: 6,
                name: "Lunar Harmony",
                description: "Balanced design featuring moon phases and celestial bodies",
                price: 59.99,
                style: "deluxe",
                image_url: "/images/product-6.png"
            },
            {
                id: 7,
                name: "Astro Mechanics",
                description: "Technical design with astronomical charts and details",
                price: 69.99,
                style: "premium",
                image_url: "/images/product-7.png"
            },
            {
                id: 8,
                name: "Cosmic Crown",
                description: "Luxurious design with ornate celestial crown motif",
                price: 79.99,
                style: "luxury",
                image_url: "/images/product-8.png"
            }
        ];
    };

    // Format price function
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    return (
        <PageContainer>
            <Container>
                <div className="products-section flex flex-col items-center justify-start w-full !bg-cover h-[70vw] md:h-[50vw] !bg-center pt-[14vw]">
                    <h2 className="text-white text-[30px] md:text-[65px] font-normal text-center leading-none">Personalized Star Maps</h2>
                    <p className="text-white font-normal text-center leading-tight max-w-[740px] mx-auto px-20 text-[14px] md:text-[20px] mt-3">
                        Choose from our collection of beautifully designed star maps. Each design captures the night sky exactly as it appeared on your special date and location.
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-[50vh]">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
                    </div>
                ) : error ? (
                    <div className="text-red-500 text-center py-8">
                        {error}
                    </div>
                ) : (
                    <div className="grid !grid-cols-2 md:!grid-cols-4 gap-8 w-full px-8 md:px-16 mt-[60px] md:mt-[120px] relative z-10">
                        {products.map(product => (
                            <div 
                                key={product.id} 
                                className="flex flex-col items-center transition-transform duration-300 hover:scale-105 cursor-pointer mb-6"
                            >
                                <img src={product.image_url} alt={product.name} className="w-full" />
                                <p className="text-2sm w-full mx-auto text-left text-[#A0A0A0] pr-[10%] mt-4">{product.name}</p>
                                <p className="text-lg w-full mx-auto text-left">{formatPrice(product.price)}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex justify-center mt-12 relative z-10">
                    <Link to="/create" className="button-new">Create Your Star Map</Link>                
                </div>

                <div className="relative">
                    <img src="/images/gradients/gradient-3.svg" alt="Gradient" className="absolute top-[-280px] left-[-120px] w-[688px] h-auto z-[5] opacity-50" />
                </div>

                <div className="relative px-8 md:px-16 pb-[120px] overflow-y-hidden">
                    <img src="/images/gradients/gradient-4.svg" alt="Gradient" className="absolute bottom-[-30px] md:bottom-[-190px] right-[-60px] w-[440px] md:w-[688px] h-auto z-[5] opacity-50" />
                
                    <div className="w-full bg-black relative z-10 rounded-xl mt-[100px] md:pl-[60px] px-8 pb-[60px] md:pr-[120px] max-w-[1480px] mx-auto pt-[1px]">
                        <h2 className='text-white text-[30px] md:text-[51px] leading-tight font-normal mx-auto relative z-10 w-full text-center mt-[10%] md:mt-[5%]'>Give the Gift of a Memorable Moment</h2>
                        <p className='text-lg md:text-lg font-normal mx-auto relative z-10 w-full text-center mt-4 text-[#A961ED]'>with Our Gift Card</p>
                        
                        <div className="grid !grid-cols-1 md:!grid-cols-2 !gap-[20px] sm:grid-cols-1 w-full">
                            <div className="w-full flex justify-center items-center">
                                <img src="/images/gift-card-big.svg" className="w-full h-auto" alt="Gift Card" />
                            </div>

                            <div className="w-full md:pl-[10%] flex flex-col justify-center items-center md:items-start">
                                <p className="text-[16px] md:text-[20px] max-w-[620px] text-center md:text-left">
                                    Gift card for creating a personalized star map! It's a unique and heartfelt way to capture an important moment in your loved one's life — a birthday, anniversary, or any special event. Make your gift more than just an item, but an unforgettable experience that will stay with them forever.
                                    <br/><br/>Choose the amount, and let them create their own starry memory!
                                </p>
                                <Link to="/gift-card" className="button-new mt-10">
                                    Buy Gift Card
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </PageContainer>
    );
};

export default ProductsPage; 